// xlsx import/template for quiz questions, powered by ExcelJS so we can read
// images that are EMBEDDED in cells (drag-drop) as well as image URLs.
//
// The parser is intentionally forgiving: it maps columns by header NAME (accent/
// case/space-insensitive) so it reads BOTH the optimized template ("Preguntas"
// sheet, Spanish headers) and Lisa's earlier format ("Question ES", "Choice 1 ES",
// "Choice 1 Image URL", "Answer ES", correct option marked with a ✅ in the text).
//
// ExcelJS is loaded dynamically so it stays out of the main app bundle.
import type { Choice, Question, QuestionType } from "./types";

export interface PendingImage {
  blob: Blob;
  filename: string;
}

export interface ParsedRow {
  rowNumber: number;
  question: Partial<Question> & { error?: string };
  /** Embedded image for the question photo, to upload before saving. */
  pendingQuestionImage?: PendingImage;
  /** Embedded images aligned by choice index (same order as question.choices). */
  pendingChoiceImages: (PendingImage | undefined)[];
}

export interface ParseResult {
  sheetName: string;
  rows: ParsedRow[];
}

const MAX_CHOICES = 8;

function norm(s: string): string {
  return s
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[\s_\-./]/g, "");
}

type ColKind =
  | { kind: "course" }
  | { kind: "type" }
  | { kind: "text" }
  | { kind: "qimage" }
  | { kind: "correct" }
  | { kind: "answer" }
  | { kind: "ref" }
  | { kind: "active" }
  | { kind: "choiceText"; idx: number }
  | { kind: "choiceImage"; idx: number }
  | { kind: "ignore" };

/** Classify a header cell into a logical field. */
function classifyHeader(raw: string): ColKind {
  const h = norm(raw);
  if (!h) return { kind: "ignore" };

  // Choice columns carry a digit.
  const digit = h.match(/(\d+)/);
  const idx = digit ? parseInt(digit[1], 10) : 0;
  const hasChoiceWord = /(choice|opcion|respuesta)/.test(h);
  const isImageWord = /(image|imagen|foto|url)/.test(h);

  if (idx >= 1 && idx <= MAX_CHOICES) {
    // e.g. "imagenopcion1", "choice1imageurl", "opcion1imagen"
    if (isImageWord && (hasChoiceWord || /imagen|image/.test(h))) {
      return { kind: "choiceImage", idx };
    }
    if (hasChoiceWord) {
      if (/en$/.test(h) && !/es$/.test(h)) return { kind: "ignore" }; // English column
      return { kind: "choiceText", idx };
    }
  }

  if (/^(curso|course|courseid)$/.test(h)) return { kind: "course" };
  if (/(tipo|type|choicetype)/.test(h)) return { kind: "type" };
  if (/(ref|externalid)/.test(h) && !h.includes("pregunta")) return { kind: "ref" };
  if (/(activa|activo|active)/.test(h)) return { kind: "active" };

  // Question image (no choice digit).
  if (isImageWord && /(pregunta|question)/.test(h)) return { kind: "qimage" };
  if (/^(imagen|imagenpregunta|questionimage|foto)$/.test(h)) return { kind: "qimage" };

  // Correct-option marker vs free-text accepted answers.
  if (/^(correcta|correct|opcioncorrecta|opcioncorrect)$/.test(h)) {
    return { kind: "correct" };
  }
  if (/(respuestatexto|respuestacorrecta|respuestasaceptadas|acceptedanswers|answeres|answer|respuesta)/.test(h)) {
    return { kind: "answer" };
  }

  // Question number / English / blank -> ignore.
  if (/(numero|number|^num$|^n$)/.test(h)) return { kind: "ignore" };
  if (/questionen/.test(h)) return { kind: "ignore" };

  // Question text (ES preferred).
  if (/^(pregunta|preguntaes|enunciado)$/.test(h)) return { kind: "text" };
  if (/(questiones|questiontext)/.test(h)) return { kind: "text" };
  if (/^question$/.test(h)) return { kind: "text" };

  return { kind: "ignore" };
}

/** Strip a "correct" marker (✅/✓/(correcta)/trailing *) and report if present. */
function extractMarker(text: string): { text: string; marked: boolean } {
  if (!text) return { text: "", marked: false };
  const marked = /[✅✓☑]|\(correcta\)/i.test(text) || /\s\*\s*$/.test(text);
  const cleaned = text
    .replace(/[✅✓☑]/g, "")
    .replace(/\(correcta\)/gi, "")
    .replace(/\s*\*\s*$/, "")
    .replace(/\s+/g, " ")
    .trim();
  return { text: cleaned, marked };
}

function makeId(courseId: string, ref: string, rowNumber: number): string {
  const base = ref
    ? `${courseId}__${ref}`
    : `${courseId}__r${rowNumber}_${Math.random().toString(36).slice(2, 8)}`;
  return base.replace(/[^a-zA-Z0-9_-]/g, "_");
}

interface HeaderMap {
  course?: number;
  text?: number;
  qimage?: number;
  correct?: number;
  answer?: number;
  ref?: number;
  active?: number;
  choiceText: Record<number, number>;
  choiceImage: Record<number, number>;
}

export async function parseQuestionsWorkbook(
  data: ArrayBuffer,
): Promise<ParseResult> {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(data);

  // Prefer a sheet named like "Preguntas"/"Questions" with a usable header;
  // otherwise the first sheet that has a recognizable header row.
  const candidates = wb.worksheets.slice().sort((a, b) => {
    const score = (n: string) => (/pregunt|question|template/i.test(n) ? 0 : 1);
    return score(a.name) - score(b.name);
  });

  for (const ws of candidates) {
    const found = readSheet(ws);
    if (found && found.rows.length > 0) return found;
  }
  // Fall back to first sheet even if empty, for a clear message.
  const first = wb.worksheets[0];
  return { sheetName: first?.name ?? "", rows: [] };

  // --- inner helpers (closure over ExcelJS workbook) ---
  function readSheet(ws: import("exceljs").Worksheet): ParseResult | null {
    // Locate the header row: the first row (within the first 8) that yields >= 2
    // mapped columns including a question-text column.
    let headerRow = 0;
    let headers: HeaderMap | null = null;
    const maxScan = Math.min(ws.rowCount, 8) || 8;
    for (let r = 1; r <= maxScan; r++) {
      const map = buildHeaderMap(ws.getRow(r));
      const mappedCount =
        (map.text ? 1 : 0) +
        Object.keys(map.choiceText).length +
        (map.answer ? 1 : 0);
      if (map.text && mappedCount >= 2) {
        headerRow = r;
        headers = map;
        break;
      }
    }
    if (!headers || !headerRow) return null;

    // Index embedded images by their anchor cell (1-based row/col).
    const imagesByCell = new Map<string, PendingImage>();
    for (const img of ws.getImages()) {
      const media = wb.getImage(Number(img.imageId));
      if (!media) continue;
      const tlRow = (img.range.tl.nativeRow ?? Math.floor(img.range.tl.row)) + 1;
      const tlCol = (img.range.tl.nativeCol ?? Math.floor(img.range.tl.col)) + 1;
      const ext = media.extension || "png";
      // ExcelJS gives a Node Buffer/Uint8Array; wrap as Blob for upload.
      const bytes = media.buffer as unknown as ArrayBuffer;
      const blob = new Blob([bytes], { type: `image/${ext === "jpeg" ? "jpeg" : ext}` });
      imagesByCell.set(`${tlRow}:${tlCol}`, { blob, filename: `img.${ext}` });
    }

    const cellText = (row: import("exceljs").Row, col?: number): string => {
      if (!col) return "";
      const v = row.getCell(col).text ?? "";
      return v.toString().trim();
    };

    const rows: ParsedRow[] = [];
    for (let r = headerRow + 1; r <= ws.rowCount; r++) {
      const row = ws.getRow(r);
      const courseId = cellText(row, headers!.course);
      const qtext = cellText(row, headers!.text);
      const qimageCell = cellText(row, headers!.qimage);
      const correctRaw = cellText(row, headers!.correct);
      const answerRaw = cellText(row, headers!.answer);
      const ref = cellText(row, headers!.ref);
      const activeRaw = cellText(row, headers!.active).toLowerCase();

      // Gather choices (text and/or image), keeping marker info.
      const choices: Choice[] = [];
      const pendingChoiceImages: (PendingImage | undefined)[] = [];
      let markedIdx = -1;
      for (let n = 1; n <= MAX_CHOICES; n++) {
        const tCol = headers!.choiceText[n];
        const iCol = headers!.choiceImage[n];
        const rawText = cellText(row, tCol);
        const { text, marked } = extractMarker(rawText);
        const urlInImageCell = cellText(row, iCol);
        const embedded = iCol ? imagesByCell.get(`${r}:${iCol}`) : undefined;
        const hasImage = !!embedded || /^https?:\/\//i.test(urlInImageCell);
        if (!text && !hasImage) continue;
        const cid = `c${choices.length + 1}`;
        choices.push({
          id: cid,
          text,
          imageUrl: /^https?:\/\//i.test(urlInImageCell) ? urlInImageCell : undefined,
          correct: false,
        });
        pendingChoiceImages.push(embedded);
        if (marked) markedIdx = choices.length - 1;
      }

      // Skip fully empty rows.
      if (!qtext && choices.length === 0 && !answerRaw && !qimageCell) continue;

      // Question photo: embedded image or URL.
      const embeddedQ = headers!.qimage
        ? imagesByCell.get(`${r}:${headers!.qimage}`)
        : undefined;
      const qImageUrl = /^https?:\/\//i.test(qimageCell) ? qimageCell : undefined;

      const type: QuestionType = choices.length >= 2 ? "choice" : "text";

      const q: Partial<Question> = {
        courseId: courseId || undefined,
        type,
        text: qtext,
        imageUrl: qImageUrl,
        ref: ref || undefined,
        active: activeRaw ? !["false", "no", "0"].includes(activeRaw) : true,
      };

      let error: string | undefined;
      if (!qtext) error = "Falta el texto de la pregunta";

      if (type === "choice") {
        // Determine the correct option: explicit number/text, else ✅ marker.
        let correctIndex = -1;
        const asNum = parseInt(correctRaw, 10);
        if (Number.isInteger(asNum) && asNum >= 1 && asNum <= choices.length) {
          correctIndex = asNum - 1;
        } else if (correctRaw) {
          const t = norm(correctRaw);
          correctIndex = choices.findIndex((c) => norm(c.text) === t);
        }
        if (correctIndex < 0) correctIndex = markedIdx;
        if (correctIndex >= 0) choices[correctIndex].correct = true;
        q.choices = choices;
        if (!error && !choices.some((c) => c.correct)) {
          error = "Marca la opción correcta (columna 'Correcta' con 1-4, o ✅)";
        }
      } else {
        const accepted = [correctRaw, answerRaw]
          .filter(Boolean)
          .flatMap((s) => s.split(/[|;\n]+/))
          .map((s) => extractMarker(s).text)
          .filter(Boolean);
        q.acceptedAnswers = accepted;
        if (!error && accepted.length === 0) {
          error = "Falta la respuesta correcta (columna 'Respuesta')";
        }
      }

      q.id = makeId(courseId || "curso", ref, r);
      rows.push({
        rowNumber: r,
        question: { ...q, error },
        pendingQuestionImage: embeddedQ,
        pendingChoiceImages,
      });
    }

    return { sheetName: ws.name, rows };
  }

  function buildHeaderMap(row: import("exceljs").Row): HeaderMap {
    const map: HeaderMap = { choiceText: {}, choiceImage: {} };
    row.eachCell({ includeEmpty: false }, (cell, col) => {
      const k = classifyHeader((cell.text ?? "").toString());
      switch (k.kind) {
        case "course": map.course = col; break;
        case "text": if (!map.text) map.text = col; break;
        case "qimage": if (!map.qimage) map.qimage = col; break;
        case "correct": map.correct = col; break;
        case "answer": if (!map.answer) map.answer = col; break;
        case "ref": map.ref = col; break;
        case "active": map.active = col; break;
        case "choiceText": if (!map.choiceText[k.idx]) map.choiceText[k.idx] = col; break;
        case "choiceImage": if (!map.choiceImage[k.idx]) map.choiceImage[k.idx] = col; break;
      }
    });
    return map;
  }
}

// ---------------------------------------------------------------------------
// Optimized template (the file Lisa fills in). Built with ExcelJS so it can
// carry dropdowns + instructions. Images are added by either dragging a picture
// INTO the image cell, or pasting an image URL.
// ---------------------------------------------------------------------------

export const TEMPLATE_HEADERS = [
  "Curso",
  "Tipo",
  "Pregunta",
  "Imagen pregunta",
  "Opción 1",
  "Imagen opción 1",
  "Opción 2",
  "Imagen opción 2",
  "Opción 3",
  "Imagen opción 3",
  "Opción 4",
  "Imagen opción 4",
  "Correcta",
  "Respuesta (texto)",
];

const ACCENT = "FF3EABBF";

export async function buildTemplateWorkbook(
  courseIds: string[] = ["galope1", "galope2"],
): Promise<Blob> {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = "Equi for All";

  // --- Instrucciones ---
  const info = wb.addWorksheet("Instrucciones");
  info.columns = [{ width: 110 }];
  const lines: [string, boolean][] = [
    ["Cómo añadir preguntas (hoja \"Preguntas\")", true],
    ["", false],
    ["• Una pregunta por fila. No cambies los nombres de las columnas.", false],
    [`• Curso: escribe el id del curso (${courseIds.join(" o ")}). Si lo dejas vacío, se usa el curso que elijas al importar.`, false],
    ["• Tipo: \"Opción múltiple\" o \"Texto\". (Si lo dejas vacío se deduce: con opciones = opción múltiple; sin opciones = texto.)", false],
    ["• Pregunta: el texto de la pregunta (en español).", false],
    ["", false],
    ["IMÁGENES (opcional, para la pregunta y/o cada opción):", true],
    ["   Opción A — arrastra la imagen DENTRO de la celda \"Imagen ...\" correspondiente.", false],
    ["   Opción B — pega un enlace (URL) de la imagen en esa celda.", false],
    ["", false],
    ["OPCIÓN MÚLTIPLE:", true],
    ["• Rellena \"Opción 1..4\" (texto) y/o sus imágenes.", false],
    ["• En \"Correcta\" escribe el número de la opción correcta (1, 2, 3 o 4).", false],
    ["", false],
    ["TEXTO (respuesta escrita):", true],
    ["• Deja las opciones vacías.", false],
    ["• En \"Respuesta (texto)\" escribe la respuesta correcta. Para varias respuestas válidas, sepáralas con | (ej.: cruz | la cruz).", false],
    ["  La corrección no distingue mayúsculas, acentos ni espacios.", false],
    ["", false],
    ["Consejo: borra las filas de ejemplo antes de importar (o déjalas, el importador las muestra para revisión).", false],
  ];
  lines.forEach(([t, bold], i) => {
    const cell = info.getCell(i + 1, 1);
    cell.value = t;
    cell.font = { name: "Arial", bold, size: bold ? 12 : 11, color: bold ? { argb: ACCENT } : undefined };
    cell.alignment = { wrapText: true, vertical: "top" };
  });

  // --- Preguntas ---
  const ws = wb.addWorksheet("Preguntas", {
    views: [{ state: "frozen", ySplit: 1 }],
  });
  ws.columns = TEMPLATE_HEADERS.map((h) => ({
    header: h,
    width: /Imagen/.test(h) ? 16 : h === "Pregunta" ? 42 : h === "Respuesta (texto)" ? 28 : 18,
  }));
  const header = ws.getRow(1);
  header.height = 22;
  header.eachCell((cell) => {
    cell.font = { name: "Arial", bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ACCENT } };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  });

  // Example rows
  ws.addRow(["galope1", "Opción múltiple", "Al montar en carretera debes…", "", "Circular por la derecha", "", "Circular por la izquierda", "", "Ir en zigzag", "", "Circular en medio", "", 1, ""]);
  ws.addRow(["galope1", "Texto", "Escribe el nombre de esta parte del caballo:", "(arrastra aquí una imagen o pega una URL)", "", "", "", "", "", "", "", "", "", "cruz | la cruz"]);
  ws.addRow(["galope2", "Opción múltiple", "¿Qué significa \"alargar el aire\"?", "", "Cambiar de mano", "", "Pasar a un aire superior", "", "Aumentar la velocidad en el mismo aire", "", "Acortar el paso", "", 3, ""]);

  // Data validations on rows 2..400
  for (let r = 2; r <= 400; r++) {
    ws.getCell(r, 1).dataValidation = {
      type: "list", allowBlank: true, formulae: [`"${courseIds.join(",")}"`],
    };
    ws.getCell(r, 2).dataValidation = {
      type: "list", allowBlank: true, formulae: ['"Opción múltiple,Texto"'],
    };
    ws.getCell(r, 13).dataValidation = {
      type: "list", allowBlank: true, formulae: ['"1,2,3,4"'],
    };
  }
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: TEMPLATE_HEADERS.length } };

  const buf = await wb.xlsx.writeBuffer();
  return new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}
